import { useState } from 'react';
import { Box, Button, IconButton, Grid, Skeleton, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import { Settings } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import DayjsAdapter from '@mui/lab/AdapterDayjs';
import i18next from 'i18next';
import TimeSelector from '../controls/TimeSelector';

export default function DashboardFooter({ dashboardState, onDateRangeChange, onOpenDrawer, loading }) {

    const { t } = useTranslation()
    const [startOpen, setStartOpen] = useState(false)
    const [endOpen, setEndOpen] = useState(false)
    const [dayOpen, setDayOpen] = useState(false)

    const dateRange = dashboardState?.dateRange
    const disabled = !dashboardState?.dataReady || !dashboardState?.minDate || !dashboardState?.maxDate // null bounds = active dataset has no range (e.g. weather tab with no weather CSV) -> calendar pickers disable

    // single-day range (drill-down, initial load, "yesterday" preset, …) -> archive-style "‹ day ›" switcher instead of the start–end pickers
    const day = dateRange?.[0]
    const isSingleDay = !!(day && dateRange?.[1]) && day.isSame(dateRange[1], 'day')
    const canStepPrev = !!day && (!dashboardState?.minDate || day.isAfter(dashboardState.minDate, 'day'))
    const canStepNext = !!day && (!dashboardState?.maxDate || day.isBefore(dashboardState.maxDate, 'day'))

    const startLabel = dateRange?.[0]?.format(t('date.format.shortest')) ?? '–'
    const endLabel = dateRange?.[1]?.format(t('date.format.shortest')) ?? '–'

    if (!dashboardState?.dataReady) {
        return (
            <Grid container columns={{ xs: 1 }}>
                <Grid item xs={1}>
                    <Box sx={{ position: "relative", width: "100%", display: "flex", justifyContent: "center" }}>
                        <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "center", height: "48px", gap: 1 }}>
                            <Skeleton variant="text" width={60} height={28} />
                            <Typography sx={{ color: '#003366', fontSize: '1.2rem' }}>–</Typography>
                            <Skeleton variant="text" width={60} height={28} />
                        </Box>
                        <Box sx={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
                            <IconButton sx={{ p: 1, pointerEvents: "all" }} onClick={onOpenDrawer} color="primary">
                                <Settings sx={{ fontSize: "1.66rem" }} />
                            </IconButton>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        )
    }

    return (
        <Grid
            container
            columns={{ xs: 1 }}
        >
            <Grid item xs={1}>
                <Box sx={{
                    position: "relative",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center"
                }}>

                    <Box sx={{
                        flexGrow: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "48px"
                    }}>
                        {isSingleDay ? (
                            <LocalizationProvider dateAdapter={DayjsAdapter} locale={i18next.language}>
                                <DatePicker
                                    open={dayOpen}
                                    onClose={() => setDayOpen(false)}
                                    value={day ?? null}
                                    onChange={(picked) => {
                                        if (picked?.isValid() && day?.isValid()) {
                                            const delta = picked.startOf('day').diff(day.startOf('day'), 'day')
                                            if (delta !== 0) dashboardState?.onStepDay?.(delta)
                                        }
                                    }}
                                    minDate={dashboardState?.minDate}
                                    maxDate={dashboardState?.maxDate}
                                    cancelText={t('actions.cancel')}
                                    toolbarTitle={t('date.select_date')}
                                    renderInput={({ inputRef }) =>
                                        <TimeSelector
                                            showDate
                                            showTime={false}
                                            timestamp={day}
                                            stepLeft={() => dashboardState?.onStepDay?.(-1)}
                                            stepRight={() => dashboardState?.onStepDay?.(1)}
                                            canStepLeft={canStepPrev}
                                            canStepRight={canStepNext}
                                            loading={loading || disabled}
                                            setPickerOpen={setDayOpen}
                                            inputRef={inputRef}
                                        />
                                    }
                                />
                            </LocalizationProvider>
                        ) : (
                            <LocalizationProvider dateAdapter={DayjsAdapter} locale={i18next.language}>
                                <DatePicker
                                    inputFormat={t('date.format.full')}
                                    mask={t('date.mask.full')}
                                    value={dateRange?.[0] ?? null}
                                    onChange={(newStart) => {
                                        if (newStart?.isValid()) {
                                            onDateRangeChange(
                                                newStart.format('YYYY-MM-DD'),
                                                dateRange?.[1]?.format('YYYY-MM-DD')
                                            )
                                        }
                                    }}
                                    minDate={dashboardState?.minDate}
                                    maxDate={dateRange?.[1]}
                                    disabled={disabled}
                                    cancelText={t('actions.cancel')}
                                    toolbarTitle={t('date.select_date')}
                                    showTodayButton
                                    todayText={t("date.today")}
                                    open={startOpen}
                                    onClose={() => setStartOpen(false)}
                                    renderInput={({ inputRef }) =>
                                        <Button
                                            component="span"
                                            ref={inputRef}
                                            disabled={disabled}
                                            onClick={() => !disabled && setStartOpen(true)}
                                            sx={{
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                p: 0,
                                                color: "black",
                                                minWidth: 0,
                                                pointerEvents: "auto"
                                            }}
                                        >
                                            <Typography className="TimeSelector-label" sx={{ fontSize: "1.6rem !important", cursor: "pointer" }}>
                                                {startLabel}
                                            </Typography>
                                        </Button>
                                    }
                                />

                                <Typography sx={{ px: 1, userSelect: 'none', color: '#003366', pointerEvents: 'none', fontSize: '1.2rem' }}>
                                    –
                                </Typography>

                                <DatePicker
                                    inputFormat={t('date.format.full')}
                                    mask={t('date.mask.full')}
                                    value={dateRange?.[1] ?? null}
                                    onChange={(newEnd) => {
                                        if (newEnd?.isValid()) {
                                            onDateRangeChange(
                                                dateRange?.[0]?.format('YYYY-MM-DD'),
                                                newEnd.format('YYYY-MM-DD')
                                            )
                                        }
                                    }}
                                    minDate={dateRange?.[0]}
                                    maxDate={dashboardState?.maxDate}
                                    disabled={disabled}
                                    cancelText={t('actions.cancel')}
                                    toolbarTitle={t('date.select_date')}
                                    showTodayButton
                                    todayText={t("date.today")}
                                    open={endOpen}
                                    onClose={() => setEndOpen(false)}
                                    renderInput={({ inputRef }) =>
                                        <Button
                                            component="span"
                                            ref={inputRef}
                                            disabled={disabled}
                                            onClick={() => !disabled && setEndOpen(true)}
                                            sx={{
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                p: 0,
                                                color: "black",
                                                minWidth: 0,
                                                pointerEvents: "auto"
                                            }}
                                        >
                                            <Typography className="TimeSelector-label" sx={{ fontSize: "1.6rem !important", cursor: "pointer" }}>
                                                {endLabel}
                                            </Typography>
                                        </Button>
                                    }
                                />
                            </LocalizationProvider>
                        )}
                    </Box>

                    <Box sx={{
                        position: "absolute",
                        right: 0,
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none"
                    }}>
                        <IconButton
                            sx={{ p: 1, pointerEvents: "all" }}
                            onClick={onOpenDrawer}
                            color="primary"
                            disabled={loading || disabled}
                        >
                            <Settings sx={{ fontSize: "1.66rem" }} />
                        </IconButton>
                    </Box>

                </Box>
            </Grid>
        </Grid>
    )
}
